// models/commerce/SupplierDailyOrderItem.js
import { Model } from 'pinia-orm';

/**
 * Canonical JSON:API resource type for the supplier daily-order line item.
 *
 * ── SINGLE SOURCE OF TRUTH (provisional) ──────────────────────────────────────
 * The design-intent BE brief proposes `supplier-box-items`
 * (../backend/_bmad-output/sync/briefs/2026-07-05-supplier-daily-orders.md); the
 * delivered slice may rename it. Anything that needs the resource-type string
 * imports THIS constant — one rename lands everywhere.
 */
export const SUPPLIER_DAILY_ORDER_ITEM_ENTITY = 'supplier-box-items';

/**
 * SupplierDailyOrderItem — one denormalized line item on the Suppliers-Club
 * Daily Orders page (design spec 2026-07-05, FE plan D-Δ4). The page renders ONE
 * flat, scrollable list of these across whatever collection day(s) are currently
 * active for the supplier; there is no box-detail view, so the box context the
 * card needs (`collectionDate`, `dayLabel`, `boxId`) is flattened ONTO each item
 * by useSupplierDailyOrders rather than fetched separately.
 *
 * PRIVACY INVARIANT (locked, brief L4): buyer/member identity is NEVER exposed.
 * `memberNote` is the ordering member's free-text note with NO identity attached
 * — same convention as SupplierWallItem.orderComment. NEVER add a member/buyer
 * relation or field to this model.
 *
 * All field names are PROVISIONAL (pinned by the delivered BE slice). Monetary
 * fields (`unitPrice`) are in PIASTRES (== cents, ÷100 = EGP) — pass straight to
 * useCurrency().formatCurrency(). Amounts on this page reverse the originally-
 * locked "no money" constraint — owner-confirmed (FE→BE addenda ask #2).
 *
 * Wire / attached attributes:
 *   - productName    string|null  denormalized product name (FE→BE ask)
 *   - variantLabel   string|null  optional size/variant chip (omit when absent)
 *   - qty            int          ordered quantity
 *   - unitPrice      int          unit price in piastres
 *   - photoUrl       string|null  product thumbnail (NOT a buyer avatar)
 *   - catalogPhotos  array        optional source-post gallery, flat URL strings
 *                                 (wire name confirmed live 2026-07-08 — see `photoUrls`)
 *   - memberNote     string|null  ordering member's note — UNTRUSTED free text,
 *                                 no identity (FE→BE addenda ask #1)
 *   - originBadge    string|null  'own_channel' | 'claimed' — whether this order
 *                                 came from the supplier's own post or was pulled
 *                                 (سحب) from another channel's post. Live BE field,
 *                                 confirmed 2026-07-08; only the 'claimed' case
 *                                 renders a badge (own-channel is the unmarked
 *                                 default, matching this card's other chips).
 *   - signal         string|null  SupplierSignal value, or null = unanswered
 *   - reservedQty    int          qty reserved (== qty for full RESERVED)
 *   - frozen         bool         BE write-gate truth (design spec Section 5)
 *   - frozenReason   string|null  SupplierFrozenReason value (why it's frozen)
 *   - collected      bool         the collector took it on this day (plan outcome)
 *   - postContent    string|null  optional source-post body (sheet, Section 3.2)
 *   - demandCount    int          optional "N members ordered this" (sheet)
 *   - boxId          string|null  CLIENT-ATTACHED — owning box id (grouping +
 *                                 poll-arming); also the {box} write path param
 *   - collectionDate string|null  CLIENT-ATTACHED — owning box's collection date
 *   - dayLabel       string|null  CLIENT-ATTACHED — derived day-divider label
 */
export default class SupplierDailyOrderItem extends Model {
  static entity = SUPPLIER_DAILY_ORDER_ITEM_ENTITY;

  static fields() {
    return {
      id: this.uid(),
      // ── Denormalized display (BE) ──
      productName: this.attr(null),
      variantLabel: this.attr(null),
      qty: this.number(0),
      unitPrice: this.number(0),
      photoUrl: this.attr(null),
      catalogPhotos: this.attr([]),
      memberNote: this.attr(null),
      originBadge: this.attr(null),
      // ── Advisory write-state (BE) ──
      signal: this.attr(null),
      reservedQty: this.number(0),
      frozen: this.boolean(false),
      frozenReason: this.attr(null),
      // The collector took this line on its day (BE `collected` = plan outcome
      // COLLECTED) — splits a finished day into taken / not taken.
      collected: this.boolean(false),
      // ── Optional source-post context (BE) ──
      postContent: this.attr(null),
      demandCount: this.number(0),
      // ── Client-attached box context (flattened by the composable) ──
      boxId: this.attr(null),
      collectionDate: this.attr(null),
      dayLabel: this.attr(null),
    };
  }

  /**
   * Line amount in piastres (qty × unitPrice). Kept as a getter so the summary
   * bar's running total and the per-item amount agree on one computation.
   */
  get lineAmount() {
    return (Number(this.qty) || 0) * (Number(this.unitPrice) || 0);
  }

  // ── Photo shape-normalizer (mirror SupplierWallPost.photoUrls) ────────────────
  // The ONLY place that knows the source-post gallery element shape (URL strings
  // vs { url }/{ src }/{ href } objects), so the card/sheet never branch on it.
  // Prefers the post gallery (`catalogPhotos`); falls back to the single product
  // thumbnail (`photoUrl`) when no gallery is present.
  get photoUrls() {
    const normalize = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map((entry) => {
          if (typeof entry === 'string') return entry;
          if (entry && typeof entry === 'object') {
            return entry.url ?? entry.src ?? entry.href ?? null;
          }
          return null;
        })
        .filter(Boolean);

    const gallery = normalize(this.catalogPhotos);
    if (gallery.length) return gallery;
    return typeof this.photoUrl === 'string' && this.photoUrl ? [this.photoUrl] : [];
  }
}
